import React, { useState, useEffect, useCallback } from 'react';
import { leadsAPI, usersAPI } from '../../services/api';

import LeadHistoryModal from './LeadHistoryModal';
import EmailHistoryModal from '../Common/EmailHistoryModal';

function LeadListing() {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [totalPipelineValue, setTotalPipelineValue] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 12
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    source: 'all',
    assignee: 'all',
    dateFrom: '',
    dateTo: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'table'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [modalCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState({});
  const [selectedSecondaryAssignees, setSelectedSecondaryAssignees] = useState([]);
  const [statusUpdate, setStatusUpdate] = useState({ 
    status: '', 
    progressPlannedEndDate: '', // When this status should end
    startDate: '', // Overall lead start date
    endDate: '', // Overall lead target end date
    description: '', // Status change description
    personName: '', // Contact person name
    designation: '' // Contact person designation
  });

  const LeadStatusOptions = [
    { name: 'Cold Lead', value: 'Cold Lead' },
    { name: 'Prospecting', value: 'Prospecting' },
    { name: 'Qualified', value: 'Qualified' },
    { name: 'Demo Scheduled / Done', value: 'Demo Scheduled / Done' },
    { name: 'Evaluation / Pilot', value: 'Evaluation / Pilot' },
    { name: 'Decision', value: 'Decision' },
    { name: 'Closed Won', value: 'Closed Won' },
    { name: 'Closed Lost', value: 'Closed Lost' },
  ];

  const LeadSourceOptions = [
    { name: 'Website', value: 'Website' },
    { name: 'Event', value: 'Event' },
    { name: 'Referral', value: 'Referral' },
    { name: 'Direct Outreach', value: 'Direct Outreach' },
    { name: 'Marketing Campaign', value: 'Marketing Campaign' },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit,
        view: viewMode
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });
      
      const [leadsResponse, usersResponse] = await Promise.all([
        leadsAPI.getAllLeads(params),
        usersAPI.getAllUsers(),
      ]);
      
      setLeads(leadsResponse.data.leads || leadsResponse.data);
      setFilteredLeads(leadsResponse.data.leads || leadsResponse.data);
      setTotalPipelineValue(leadsResponse.data.totalPipelineValue || 0);
      if (leadsResponse.data.pagination) {
        setPagination(prev => ({ ...prev, ...leadsResponse.data.pagination }));
      }
      setUsers(usersResponse.data.users || usersResponse.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [viewMode, filters, pagination.currentPage, pagination.limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      source: 'all',
      assignee: 'all',
      dateFrom: '',
      dateTo: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const deleteLead = async (id) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      setLoading(true);
      try {
        await leadsAPI.deleteLead(id);
        setLeads(leads.filter((lead) => lead._id !== id));
        setFilteredLeads(filteredLeads.filter((lead) => lead._id !== id));
        setSuccess('Lead deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete lead');
      } finally {
        setLoading(false);
      }
    }
  };

  const openStatusModal = (lead) => {
    setSelectedLead(lead);
    setStatusUpdate({ 
      status: lead.progressStatus, 
      progressPlannedEndDate: lead.progressPlannedEndDate ? new Date(lead.progressPlannedEndDate).toISOString().split('T')[0] : '', 
      startDate: lead.startDate ? new Date(lead.startDate).toISOString().split('T')[0] : '', 
      endDate: lead.endDate ? new Date(lead.endDate).toISOString().split('T')[0] : '', 
      description: '',
      personName: lead.personName || '',
      designation: lead.designation || ''
    });
    setShowStatusModal(true);
  };

  const updateLeadStatus = async () => {
    if (!statusUpdate.description.trim()) {
      setError('Description is required');
      return;
    }
    
    setLoading(true);
    try {
      let archiveReason = '';
      let rating = null;
      let ratingReason = '';

      if (statusUpdate.status === 'Closed Lost') {
        archiveReason = prompt(`Please provide a reason for marking this lead as ${statusUpdate.status}:`);
        if (!archiveReason) {
          setLoading(false);
          return;
        }
      }

      const ratingInput = prompt(`Please provide a rating (0-10) for this status change to ${statusUpdate.status}:`);
      if (ratingInput === null) {
        setLoading(false);
        return;
      }
      rating = parseFloat(ratingInput) || 0;
      
      ratingReason = prompt('Please provide a reason for this rating (optional):') || `Rating for status change to ${statusUpdate.status}`;

      await leadsAPI.updateLead(selectedLead._id, { 
        progressStatus: statusUpdate.status, 
        progressPlannedEndDate: statusUpdate.progressPlannedEndDate,
        startDate: statusUpdate.startDate,
        endDate: statusUpdate.endDate,
        statusDescription: statusUpdate.description,
        personName: statusUpdate.personName,
        designation: statusUpdate.designation,
        archiveReason, 
        rating, 
        ratingReason 
      });
      setSuccess('Lead status updated successfully!');
      setError('');
      setShowStatusModal(false);
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update lead status');
    } finally {
      setLoading(false);
    }
  };

  const updatePrimaryAssignee = async (id, primaryAssignee) => {
    setLoading(true);
    try {
      await leadsAPI.updateLead(id, { primaryAssignee });
      setSuccess('Primary assignee updated successfully!');
      setError('');
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update primary assignee');
    } finally {
      setLoading(false);
    }
  };

  const updateSecondaryAssignees = async (id, secondaryAssignees) => {
    setLoading(true);
    try {
      const response = await leadsAPI.updateLead(id, { secondaryAssignees });
      console.log('Update response:', response);
      setSuccess('Secondary assignees updated successfully!');
      setError('');
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Update error:', err);
      setError(err.response?.data?.message || 'Failed to update secondary assignees');
    } finally {
      setLoading(false);
    }
  };

  const openAssigneeModal = (lead) => {
    setSelectedLead(lead);
    setSelectedSecondaryAssignees(lead.secondaryAssignees?.map(a => a._id) || []);
    setShowAssigneeModal(true);
  };

  const handleSecondaryAssigneeChange = (userId) => {
    setSelectedSecondaryAssignees(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const saveSecondaryAssignees = async () => {
    try {
      await updateSecondaryAssignees(selectedLead._id, selectedSecondaryAssignees);
      setShowAssigneeModal(false);
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  const updateExpectedValue = async (id, expectedValue) => {
    setLoading(true);
    try {
      await leadsAPI.updateExpectedValue(id, expectedValue);
      setSuccess('Expected value updated successfully!');
      setError('');
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update expected value');
    } finally {
      setLoading(false);
    }
  };

  const openLeadHistoryModal = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  const openEditModal = (lead) => {
    setSelectedLead(lead);
    setEditData({
      schoolName: lead.schoolName || '',
      personName: lead.personName || '',
      designation: lead.designation || '',
      email: lead.email || '',
      phoneNumber: lead.phoneNumber || '',
      address: lead.address || '',
      expectedValue: lead.expectedValue || 0,
      source: lead.source || 'Website',
      statusDescription: lead.statusDescription || ''
    });
    setShowEditModal(true);
  };

  const updateLeadInfo = async () => {
    setLoading(true);
    try {
      await leadsAPI.updateLead(selectedLead._id, editData);
      setSuccess('Lead information updated successfully!');
      setError('');
      setShowEditModal(false);
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-purple-600 bg-clip-text text-transparent">
              Lead Listing
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage and track all leads in the system</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{success}</span>
          </div>
        )}

        {/* Filters */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-3 sm:p-4 mb-6 border border-gray-200/50">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <input
              type="text"
              placeholder="Search by school name..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base transition-all duration-200 hover:border-blue-400"
            />
            
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base transition-all duration-200 hover:border-blue-400"
            >
              <option value="all">All Statuses</option>
              {LeadStatusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.name}
                </option>
              ))}
            </select>
            
            <select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base transition-all duration-200 hover:border-blue-400"
            >
              <option value="all">All Sources</option>
              {LeadSourceOptions.map((source) => (
                <option key={source.value} value={source.value}>
                  {source.name}
                </option>
              ))}
            </select>
            
            <select
              value={filters.assignee}
              onChange={(e) => handleFilterChange('assignee', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base transition-all duration-200 hover:border-blue-400"
            >
              <option value="all">All Assignees</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.username}
                </option>
              ))}
            </select>
            
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                handleFilterChange('sortBy', sortBy);
                handleFilterChange('sortOrder', sortOrder);
              }}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base transition-all duration-200 hover:border-blue-400"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="schoolName-asc">School Name A-Z</option>
              <option value="schoolName-desc">School Name Z-A</option>
            </select>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base transition-all duration-200 hover:border-blue-400"
            />
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base transition-all duration-200 hover:border-blue-400"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={clearFilters}
                className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-3 sm:px-4 py-2 rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-200 text-sm sm:text-base font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Clear Filters
              </button>
              <button
                onClick={() => setViewMode(viewMode === 'list' ? 'table' : 'list')}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 sm:px-4 py-2 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 text-sm sm:text-base font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {viewMode === 'list' ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 6h18m-9 8h9" />
                    </svg>
                    Table View
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    List View
                  </>
                )}
              </button>
            </div>
            <div className="text-xs sm:text-sm text-gray-600 text-right">
              <div className="font-medium">Showing {filteredLeads.length} of {pagination.totalCount} leads</div>
              <div className="font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg mt-1">
                Pipeline: ₹{totalPipelineValue.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-600 font-medium">Loading leads...</span>
            </div>
          </div>
        )}

        {/* Leads Display */}
        {viewMode === 'table' ? (
          /* Table View */
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-xl">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Value</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pipeline Value</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Primary Assignee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLeads.map((lead) => (
                  <tr key={lead._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{lead.schoolName}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{lead.email}</div>
                      <div className="text-sm text-gray-500">{lead.phoneNumber}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.progressStatus === 'Closed Won' ? 'bg-green-100 text-green-800' :
                        lead.progressStatus === 'Closed Lost' ? 'bg-red-100 text-red-800' :
                        lead.progressStatus === 'Cold Lead' ? 'bg-gray-100 text-gray-800' :
                        lead.progressStatus === 'Prospecting' ? 'bg-blue-100 text-blue-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {lead.progressStatus}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{lead.source}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">₹{(lead.expectedValue || 0).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">₹{(lead.pipelineValue || 0).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {lead.primaryAssignee ? lead.primaryAssignee.username : 'Unassigned'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-1">
                        <button
                          onClick={() => openLeadHistoryModal(lead)}
                          className="text-blue-600 hover:text-blue-900 text-xs px-2 py-1 bg-blue-100 rounded"
                        >
                          History
                        </button>
                        <button
                          onClick={() => openEditModal(lead)}
                          className="text-green-600 hover:text-green-900 text-xs px-2 py-1 bg-green-100 rounded"
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* List View */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {filteredLeads.map((lead) => (
            <div key={lead._id} className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 group">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">{lead.schoolName}</h3>
                  {lead.personName && (
                    <p className="text-xs sm:text-sm text-gray-700 font-medium truncate">
                      {lead.personName} {lead.designation && `(${lead.designation})`}
                    </p>
                  )}
                  <p className="text-xs sm:text-sm text-gray-600 truncate">{lead.email}</p>
                </div>
                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                  lead.progressStatus === 'Closed Won' ? 'bg-green-100 text-green-800 border border-green-200' :
                  lead.progressStatus === 'Closed Lost' ? 'bg-red-100 text-red-800 border border-red-200' :
                  lead.progressStatus === 'Cold Lead' ? 'bg-gray-100 text-gray-800 border border-gray-200' :
                  lead.progressStatus === 'Prospecting' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                  'bg-purple-100 text-purple-800 border border-purple-200'
                }`}>
                  {lead.progressStatus}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Phone:</span> {lead.phoneNumber}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Source:</span> {lead.source}
                </p>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Assignees:</span>
                  <div className="mt-1 space-y-1">
                    {lead.primaryAssignee && (
                      <div className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs mr-1">
                        Primary: {lead.primaryAssignee.username}
                      </div>
                    )}
                    {lead.secondaryAssignees && lead.secondaryAssignees.map((assignee, idx) => (
                      <div key={idx} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs mr-1">
                        Secondary: {assignee.username}
                      </div>
                    ))}
                    {!lead.primaryAssignee && (!lead.secondaryAssignees || lead.secondaryAssignees.length === 0) && (
                      <span className="text-gray-500">Unassigned</span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Expected Value:</span> ₹{(lead.expectedValue || 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Pipeline Value:</span> ₹{(lead.pipelineValue || 0).toFixed(2)}
                </p>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Status Period:</span>
                  <div className="text-xs mt-1">
                    Status End: {lead.progressPlannedEndDate ? new Date(lead.progressPlannedEndDate).toLocaleDateString() : 'N/A'}
                  
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Status:</label>
                  <button
                    onClick={() => openStatusModal(lead)}
                    className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                  >
                    Update Status
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Expected Value:</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={lead.expectedValue || 0}
                    onChange={(e) => updateExpectedValue(lead._id, parseFloat(e.target.value) || 0)}
                    className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-24"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <label className="text-xs font-medium text-gray-700">Primary:</label>
                    <select
                      value={lead.primaryAssignee?._id || ''}
                      onChange={(e) => updatePrimaryAssignee(lead._id, e.target.value)}
                      className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 flex-1"
                    >
                      <option value="">None</option>
                      {users.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.username}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-xs font-medium text-gray-700">Secondary:</label>
                    <button
                      onClick={() => openAssigneeModal(lead)}
                      className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition flex-1"
                    >
                      Manage ({lead.secondaryAssignees?.length || 0})
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => openLeadHistoryModal(lead)}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-xs sm:text-sm font-medium shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  History
                </button>
                <button
                  onClick={() => openEditModal(lead)}
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-2 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 text-xs sm:text-sm font-medium shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteLead(lead._id)}
                  className="bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-2 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 text-xs sm:text-sm font-medium shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  Delete
                </button>
              </div>
            </div>
            ))}
          </div>
        )}

        {!loading && filteredLeads.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No leads found</h3>
            <p className="text-sm sm:text-base text-gray-500 mb-4">Try adjusting your filters or create a new lead.</p>
            <button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105">
              Create New Lead
            </button>
          </div>
        )}

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="flex flex-wrap justify-center items-center gap-2 mt-8">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrev}
              className="px-3 sm:px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm sm:text-base font-medium shadow-sm hover:shadow-md"
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const pageNum = Math.max(1, pagination.currentPage - 2) + i;
              if (pageNum > pagination.totalPages) return null;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 sm:px-4 py-2 rounded-xl border transition-all duration-200 text-sm sm:text-base font-medium shadow-sm hover:shadow-md ${
                    pageNum === pagination.currentPage
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white border-blue-500 shadow-lg'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
              className="px-3 sm:px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm sm:text-base font-medium shadow-sm hover:shadow-md"
            >
              Next
            </button>
          </div>
        )}

        {/* Modals */}
        {selectedLead && showLeadModal && (
          <LeadHistoryModal
            lead={selectedLead}
            onClose={() => setShowLeadModal(false)}
            users={users}
            onUpdateHistory={async (leadId, historyId, description) => {
              try {
                if (leadId && historyId && description) {
                  await leadsAPI.updateLeadHistory(leadId, historyId, { description });
                  setSuccess('History updated successfully!');
                } else {
                  setSuccess('Rating updated successfully!');
                }
                await fetchData();
                setTimeout(() => setSuccess(''), 3000);
              } catch (err) {
                setError(err.response?.data?.message || 'Failed to update');
              }
            }}
          />
        )}
        {selectedLead && showEmailModal && (
          <EmailHistoryModal
            lead={selectedLead}
            category={modalCategory}
            onClose={() => setShowEmailModal(false)}
          />
        )}
        
        {/* Status Update Modal */}
        {showStatusModal && selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Update Lead Status</h3>
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={statusUpdate.status}
                      onChange={(e) => setStatusUpdate({...statusUpdate, status: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {LeadStatusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status End Date</label>
                    <input
                      type="date"
                      value={statusUpdate.progressPlannedEndDate}
                      onChange={(e) => setStatusUpdate({...statusUpdate, progressPlannedEndDate: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                    <input
                      type="text"
                      value={statusUpdate.personName}
                      onChange={(e) => setStatusUpdate({...statusUpdate, personName: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Contact person name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                    <input
                      type="text"
                      value={statusUpdate.designation}
                      onChange={(e) => setStatusUpdate({...statusUpdate, designation: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Principal, Director, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Lead Start Date</label>
                    <input
                      type="date"
                      value={statusUpdate.startDate}
                      onChange={(e) => setStatusUpdate({...statusUpdate, startDate: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Target End Date</label>
                    <input
                      type="date"
                      value={statusUpdate.endDate}
                      onChange={(e) => setStatusUpdate({...statusUpdate, endDate: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                  <textarea
                    value={statusUpdate.description}
                    onChange={(e) => setStatusUpdate({...statusUpdate, description: e.target.value})}
                    className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-20"
                    placeholder="Describe the status change..."
                    required
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={updateLeadStatus}
                    disabled={loading}
                    className="flex-1 bg-blue-500 text-white px-4 py-2.5 rounded-lg hover:bg-blue-600 transition disabled:opacity-50 font-medium"
                  >
                    {loading ? 'Updating...' : 'Update Status'}
                  </button>
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Edit Lead Modal */}
        {showEditModal && selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold">Edit Lead Information</h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">School Name</label>
                    <input
                      type="text"
                      value={editData.schoolName}
                      onChange={(e) => setEditData({...editData, schoolName: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                    <input
                      type="text"
                      value={editData.personName}
                      onChange={(e) => setEditData({...editData, personName: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                    <input
                      type="text"
                      value={editData.designation}
                      onChange={(e) => setEditData({...editData, designation: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({...editData, email: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={editData.phoneNumber}
                      onChange={(e) => setEditData({...editData, phoneNumber: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <input
                      type="text"
                      value={editData.address}
                      onChange={(e) => setEditData({...editData, address: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expected Value</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editData.expectedValue}
                      onChange={(e) => setEditData({...editData, expectedValue: parseFloat(e.target.value) || 0})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
                    <select
                      value={editData.source}
                      onChange={(e) => setEditData({...editData, source: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {LeadSourceOptions.map((source) => (
                        <option key={source.value} value={source.value}>
                          {source.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status Description</label>
                    <input
                      type="text"
                      value={editData.statusDescription}
                      onChange={(e) => setEditData({...editData, statusDescription: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional description for current status"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={updateLeadInfo}
                    disabled={loading}
                    className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Lead'}
                  </button>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Secondary Assignee Modal */}
        {showAssigneeModal && selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">Manage Secondary Assignees</h3>
                <button
                  onClick={() => setShowAssigneeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                {users.filter(user => user._id !== selectedLead.primaryAssignee?._id).map((user) => (
                  <label key={user._id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={selectedSecondaryAssignees.includes(user._id)}
                      onChange={() => handleSecondaryAssigneeChange(user._id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{user.username}</span>
                  </label>
                ))}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={saveSecondaryAssignees}
                  disabled={loading}
                  className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setShowAssigneeModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default LeadListing;